/**
 * The hover pill on every wall card and Pinned tile (Part 07 decision 7,
 * mockup `.hov`): Open · Bookmark · More, shown while the pointer is over the
 * item or keyboard focus is in it. Open is the item's handle (a click
 * opens, ⌘/Ctrl- or Shift-click selects, a right-click opens the menu);
 * Bookmark (keeps the item in the wall's top row) shows only where
 * `canPinFrame` allows it, pressed while bookmarked; More opens
 * the card menu at the button.
 */
import { Button, Icons } from "@/components/ui";
import {
  setFramePinned,
  useCanPinFrame,
  useFramePinned,
} from "@/lib/frame-pinning";
import { useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { wallMessages } from "@/i18n/messages/wall";
import type { ContextMenuAnchor } from "@/components/ui/context-menu";
import { wallCardPressHandlers } from "./wall-selection";

interface WallCardActionsProps {
  id: string;
  title: string;
  /** Part of a multi-selection: the Open handle says so. */
  selected: boolean;
  onOpen: (id: string) => void;
  onMenu: (id: string, anchor: ContextMenuAnchor) => void;
}

/**
 * The pill's place and motion: top right, shown while the pointer is over
 * the item or focus is in it (`.denker-frame-hover-pill` paints it).
 */
export const WALL_HOVER_PILL_CLASS =
  "denker-frame-hover-pill pointer-events-none absolute right-3 top-3 z-[4] flex items-center gap-0.5 rounded-full p-[3px] opacity-0 transition-opacity duration-150 motion-safe:-translate-y-[3px] motion-safe:transition-[opacity,transform] group-focus-within/wall-item:pointer-events-auto group-focus-within/wall-item:translate-y-0 group-focus-within/wall-item:opacity-100 group-hover/wall-item:pointer-events-auto group-hover/wall-item:translate-y-0 group-hover/wall-item:opacity-100";

export const WALL_PILL_BUTTON_CLASS = "h-7 w-7";

export function WallCardActions({
  id,
  title,
  selected,
  onOpen,
  onMenu,
}: WallCardActionsProps) {
  const canPin = useCanPinFrame(id);
  const pinned = useFramePinned(id);
  const t = useT(wallMessages);
  const tCommon = useT(commonMessages);
  const tTasks = useT(panelsTasksMessages);
  const pinLabel = pinned ? t("removeBookmark") : t("bookmark");
  return (
    <div
      className={WALL_HOVER_PILL_CLASS}
      data-denker-glass-force="css"
      data-interactive="true"
      data-testid={`wall-card-actions-${id}`}
    >
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={WALL_PILL_BUTTON_CLASS}
        aria-label={t(selected ? "openItemSelected" : "openItem", { title })}
        title={tCommon("open")}
        data-testid={`wall-card-open-${id}`}
        {...wallCardPressHandlers(id, onOpen, onMenu)}
      >
        <Icons.Maximize className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
      {canPin ? (
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          className={WALL_PILL_BUTTON_CLASS}
          aria-label={pinLabel}
          aria-pressed={pinned}
          title={pinLabel}
          onClick={() => setFramePinned(id, !pinned)}
          data-testid={`wall-card-pin-${id}`}
        >
          <Icons.Bookmark className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={WALL_PILL_BUTTON_CLASS}
        aria-label={tTasks("more")}
        title={tTasks("more")}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          onMenu(id, { rect, align: "start" });
        }}
        data-testid={`wall-card-more-${id}`}
      >
        <Icons.MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>
    </div>
  );
}
